import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  FaBuilding,
  FaChartBar,
  FaChevronDown,
  FaGlobe,
  FaProjectDiagram,
  FaRobot,
  FaWhatsapp,
} from "react-icons/fa";
import ConsultationModal from "./ConsultationModal";
import servicesData from "../data/servicesData";
import styles from "../styles/Navbar5.module.css";

const navLinks = [
  {
    name: "Home",
    path: "/",
  },
  {
    name: "About",
    path: "/about",
  },
  {
    name: "Services",
    path: "/services",
    type: "services",
  },
  {
    name: "Case Studies",
    type: "button",
  },
  {
    name: "Contact",
    path: "/contact",
  },
];

const serviceIconMap = {
  "government-portal-development": FaBuilding,
  "whatsapp-automation": FaWhatsapp,
  "mis-dashboards": FaChartBar,
  "workflow-automation": FaProjectDiagram,
  "ai-intelligent-automation": FaRobot,
  "global-software-services": FaGlobe,
};

const NavBar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeLink, setActiveLink] = useState("");
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isConsultationOpen, setIsConsultationOpen] = useState(false);
  const navRef = useRef(null);
  const consultationTriggerRef = useRef(null);

  const location = useLocation();

  useEffect(() => {
    const activeNavLink = navLinks.find(
      (link) => link.path === location.pathname
    );

    setActiveLink(activeNavLink ? activeNavLink.name : "");
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setIsMenuOpen(false);
        setIsServicesOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
    };
  }, []);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsServicesOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  const closeMenu = () => {
    setIsMenuOpen(false);
    setIsServicesOpen(false);
  };

  const openConsultationModal = (event) => {
    consultationTriggerRef.current = event.currentTarget;
    closeMenu();
    setIsConsultationOpen(true);
  };

  const closeConsultationModal = () => {
    setIsConsultationOpen(false);
    window.setTimeout(() => {
      consultationTriggerRef.current?.focus();
    }, 0);
  };

  return (
    <>
      <nav className={styles.navBar} ref={navRef}>
        <div className={styles.navContent}>
          <div className={styles.logoContainer}>
            <Link
              to="/"
              className={styles.logoLink}
              onClick={closeMenu}
            >
              <img
                src="/logo.png"
                alt="VishvaConnect Technologies LLP"
                className={styles.logoImage}
                width="66"
                height="66"
              />

              <div className={styles.logoTextContainer}>
                <span className={styles.companyName}>
                  VISHVACONNECT
                </span>

                <span className={styles.companySubName}>
                  TECHNOLOGIES LLP
                </span>
              </div>
            </Link>
          </div>

          <div className={styles.navigationArea}>
            <ul
              className={`${styles.navLinks} ${
                isMenuOpen ? styles.menuActive : ""
              }`}
            >
              {navLinks.map((link) => (
                <li
                  key={link.name}
                  className={`${styles.navItem} ${
                    link.type === "services" ? styles.servicesItem : ""
                  }`}
                >
                  {link.type === "button" ? (
                    <button
                      type="button"
                      className={`${styles.navLink} ${styles.navButton}`}
                      onClick={closeMenu}
                    >
                      <span>{link.name}</span>
                    </button>
                  ) : link.type === "services" ? (
                    <>
                      <button
                        type="button"
                        className={`${styles.navLink} ${
                          activeLink === link.name
                            ? styles.activeLink
                            : ""
                        } ${styles.servicesToggle}`}
                        onClick={() => {
                          setActiveLink(link.name);
                          setIsServicesOpen((previous) => !previous);
                        }}
                        aria-expanded={isServicesOpen}
                        aria-haspopup="true"
                        aria-label="Open services menu"
                      >
                        <span>{link.name}</span>
                        <FaChevronDown
                          className={`${styles.servicesChevron} ${
                            isServicesOpen ? styles.servicesChevronOpen : ""
                          }`}
                          aria-hidden="true"
                        />
                      </button>

                      <div
                        className={`${styles.servicesDropdown} ${
                          isServicesOpen ? styles.servicesDropdownOpen : ""
                        }`}
                      >
                        {servicesData.map((service) => {
                          const Icon = serviceIconMap[service.id];

                          return (
                            <Link
                              key={service.title}
                              to={service.path}
                              className={styles.servicesDropdownLink}
                              onClick={closeMenu}
                            >
                              <Icon
                                className={styles.servicesDropdownIcon}
                                aria-hidden="true"
                              />

                              <span className={styles.servicesDropdownText}>
                                <span className={styles.servicesDropdownTitle}>
                                  {service.title}
                                </span>

                                <span className={styles.servicesDropdownDescription}>
                                  {service.dropdownDescription}
                                </span>
                              </span>
                            </Link>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <Link
                      to={link.path}
                      className={`${styles.navLink} ${
                        activeLink === link.name
                          ? styles.activeLink
                          : ""
                      }`}
                      onClick={() => {
                        setActiveLink(link.name);
                        closeMenu();
                      }}
                    >
                      <span>{link.name}</span>
                    </Link>
                  )}
                </li>
              ))}

              <li className={styles.mobileConsultationItem}>
                <button
                  type="button"
                  className={styles.mobileConsultationButton}
                  onClick={openConsultationModal}
                >
                  Book a Free Consultation
                </button>
              </li>
            </ul>

            <button
              type="button"
              className={styles.consultationButton}
              onClick={openConsultationModal}
              aria-label="Book a free consultation"
            >
              Book a Free Consultation
            </button>
          </div>

          <button
            type="button"
            className={`${styles.hamburger} ${
              isMenuOpen ? styles.hamburgerActive : ""
            }`}
            onClick={() => {
              setIsMenuOpen((previous) => {
                if (previous) {
                  setIsServicesOpen(false);
                }

                return !previous;
              });
            }}
            aria-label="Toggle navigation menu"
            aria-expanded={isMenuOpen}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </nav>

      <ConsultationModal
        isOpen={isConsultationOpen}
        onClose={closeConsultationModal}
      />
    </>
  );
};

export default NavBar;
